import React, { useState } from 'react';
import {
  Box,
  ListItem,
  Typography,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  Avatar,
  Button,
  ButtonGroup,
} from '@mui/material';
import SearchHitsDialog from '../components/SearchHitsDialog';
import {
  TrendingUp,
  TrendingDown,
  ContentCopy,
  AccountCircle,
} from '@mui/icons-material';

// ─── Formatters ──────────────────────────────────────────────────────────────

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '-';
  if (typeof num === 'number') return num.toFixed(decimals);
  return '-';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

// ─── Wallet Profile ───────────────────────────────────────────────────────────

const WalletProfileSection = ({ wallet }) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Placeholder avatar since Kalshi wallets have no pfp field */}
        <Avatar
          sx={{
            width: 56,
            height: 56,
            backgroundColor: 'primary.light',
            flexShrink: 0,
          }}
        >
          <AccountCircle sx={{ fontSize: '2rem', color: 'primary.contrastText' }} />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {wallet.user_id || 'Unknown User'}
            </Typography>
            {wallet.protocol && (
              <Chip
                label={wallet.protocol}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>

          {/* Copyable user ID */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              backgroundColor: 'grey.100',
              borderRadius: 1,
              cursor: 'pointer',
              wordBreak: 'break-all',
            }}
            onClick={() => copyToClipboard(wallet.user_id)}
          >
            <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1 }}>
              {wallet.user_id}
            </Typography>
            <ContentCopy sx={{ fontSize: '0.9rem', flexShrink: 0 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Semantic Embeddings ─────────────────────────────────────────────────────

const SemanticEmbeddingsSection = ({ wallet, onEmbeddingClick }) => {
  const embeddings = [];

  if (wallet.sem_embed || wallet.text_vector) {
    embeddings.push({ key: 'sem_embed', label: 'Semantic Embedding' });
  }

  for (let i = 1; i <= 5; i++) {
    const embedKey = `embed${i}`;
    if (wallet[embedKey]) {
      embeddings.push({ key: embedKey, label: `Cluster ${i}` });
    }
  }

  if (embeddings.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <ButtonGroup size="small" variant="outlined">
        {embeddings.map((embed) => (
          <Button key={embed.key} onClick={() => onEmbeddingClick(embed.key, embed.label)}>
            {embed.label}
          </Button>
        ))}
      </ButtonGroup>
    </Box>
  );
};

// ─── Performance Metrics ──────────────────────────────────────────────────────

const PerformanceMetricsSection = ({ wallet }) => {
  const isProfitable = (wallet.pnl ?? 0) >= 0;
  const ProfitIcon = isProfitable ? TrendingUp : TrendingDown;
  const profitColor = isProfitable ? '#2e7d32' : '#d32f2f';

  const MetricBox = ({ label, value, color }) => (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        backgroundColor: 'grey.50',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: color || 'text.primary' }}>
        {value}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Metrics
      </Typography>
      <Grid container spacing={1}>

        {/* PnL */}
        <Grid>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <ProfitIcon sx={{ fontSize: '1rem', color: profitColor }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                PnL
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: profitColor }}>
              {formatCurrency(wallet.pnl)}
            </Typography>
          </Paper>
        </Grid>

        {/* Volume (quote / dollars) */}
        {wallet.volume_quote !== null && wallet.volume_quote !== undefined && (
          <Grid>
            <MetricBox label="Volume ($)" value={formatCurrency(wallet.volume_quote / 1e6)} />
          </Grid>
        )}

        {/* Volume (outcome / contracts) */}
        {wallet.volume_outcome !== null && wallet.volume_outcome !== undefined && (
          <Grid>
            <MetricBox label="Volume (contracts)" value={formatNumber(wallet.volume_outcome / 1e6, 2)} />
          </Grid>
        )}

        {/* Markets traded */}
        {wallet.markets_traded !== null && wallet.markets_traded !== undefined && (
          <Grid>
            <MetricBox label="Markets Traded" value={wallet.markets_traded} />
          </Grid>
        )}

        {/* Events per day */}
        {wallet.events_per_day !== null && wallet.events_per_day !== undefined && (
          <Grid>
            <MetricBox label="Events/Day" value={formatNumber(wallet.events_per_day, 2)} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// ─── AI Labels ────────────────────────────────────────────────────────────────

const AILabelsSection = ({ wallet }) => {
  const primaryLabels = wallet.primary_labels || [];
  const secondaryLabels = wallet.secondary_labels || [];

  // ai_labels can be a single string or array
  const aiLabels = wallet.ai_labels
    ? Array.isArray(wallet.ai_labels)
      ? wallet.ai_labels
      : [wallet.ai_labels]
    : [];

  const hasArrayData = primaryLabels.length > 0 || secondaryLabels.length > 0 || aiLabels.length > 0;

  // Only use numbered label_01..label_10 as a fallback when no array fields are populated
  const numberedLabels = [];
  if (!hasArrayData) {
    for (let i = 1; i <= 10; i++) {
      const key = `label_${String(i).padStart(2, '0')}`;
      if (wallet[key]) numberedLabels.push(wallet[key]);
    }
  }

  const hasAny = hasArrayData || numberedLabels.length > 0;
  if (!hasAny) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        AI Labels
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {primaryLabels.map((label, idx) => (
          <Chip key={`primary-${idx}`} label={label} color="secondary" size="small" variant="outlined" />
        ))}
        {secondaryLabels.map((label, idx) => (
          <Chip key={`secondary-${idx}`} label={label} color="warning" size="small" variant="outlined" />
        ))}
        {aiLabels.map((label, idx) => (
          <Chip key={`ai-${idx}`} label={label} color="info" size="small" variant="outlined" />
        ))}
        {numberedLabels.map((label, idx) => (
          <Chip key={`num-${idx}`} label={label} color="default" size="small" variant="outlined" />
        ))}
      </Box>
    </Box>
  );
};

// ─── Tags ─────────────────────────────────────────────────────────────────────

const TagsSection = ({ wallet }) => {
  const primaryTags = wallet.primary_tags || [];
  const secondaryTags = wallet.secondary_tags || [];

  const hasArrayData = primaryTags.length > 0 || secondaryTags.length > 0;

  // Only use numbered tag_01..tag_10 as a fallback when no array fields are populated
  const numberedTags = [];
  if (!hasArrayData) {
    for (let i = 1; i <= 10; i++) {
      const key = `tag_${String(i).padStart(2, '0')}`;
      if (wallet[key]) numberedTags.push(wallet[key]);
    }
  }

  const hasAny = hasArrayData || numberedTags.length > 0;
  if (!hasAny) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Tags
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {primaryTags.map((tag, idx) => (
          <Chip key={`primary-tag-${idx}`} label={tag} color="success" size="small" variant="outlined" />
        ))}
        {secondaryTags.map((tag, idx) => (
          <Chip key={`secondary-tag-${idx}`} label={tag} color="info" size="small" variant="outlined" />
        ))}
        {numberedTags.map((tag, idx) => (
          <Chip key={`num-tag-${idx}`} label={tag} color="default" size="small" variant="outlined" />
        ))}
      </Box>
    </Box>
  );
};

// ─── Timestamps ───────────────────────────────────────────────────────────────

const TimestampsSection = ({ wallet }) => {
  const hasAny = wallet.updated_at || wallet.last_trade_time;
  if (!hasAny) return null;

  const MetricBox = ({ label, value }) => (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        backgroundColor: 'grey.50',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Timestamps
      </Typography>
      <Grid container spacing={1}>
        {wallet.updated_at && (
          <Grid>
            <MetricBox label="Updated" value={formatDate(wallet.updated_at)} />
          </Grid>
        )}
        {wallet.last_trade_time && (
          <Grid>
            <MetricBox label="Last Trade" value={formatDate(wallet.last_trade_time)} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// ─── Document Info ────────────────────────────────────────────────────────────

const DocumentInfoSection = ({ hit, wallet }) => {
  const walletId = wallet.user_id || hit._id;
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          wordBreak: 'break-all',
          fontSize: '0.7rem',
          fontFamily: 'monospace',
        }}
      >
        #{walletId}
      </Typography>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const KalshiWalletCard = ({ hit }) => {
  const { _source: wallet } = hit;
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [selectedEmbeddingLabel, setSelectedEmbeddingLabel] = useState(null);
  const [selectedEmbeddingKey, setSelectedEmbeddingKey] = useState(null);

  const handleEmbeddingClick = (embedKey, label) => {
    setSelectedEmbeddingKey(embedKey);
    setSelectedEmbeddingLabel(label);
    setSearchDialogOpen(true);
  };

  const handleSearchDialogClose = () => {
    setSearchDialogOpen(false);
    setSelectedEmbeddingLabel(null);
    setSelectedEmbeddingKey(null);
  };

  const buildSearchPayload = () => ({
    index: 'kalshi-wallets-v20260315',
    origin: 'frontend',
    feed_type: 'semantic',
    include_vector: false,
    size: 10,
    vector: wallet[selectedEmbeddingKey],
  });

  const searchPayload = searchDialogOpen ? buildSearchPayload() : null;

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0,
      }}
    >
      <Card
        elevation={1}
        sx={{
          width: '100%',
          backgroundColor: '#fafafa',
          overflow: 'visible',
        }}
      >
        <CardContent sx={{ pb: 2 }}>
          <WalletProfileSection wallet={wallet} />
          <SemanticEmbeddingsSection wallet={wallet} onEmbeddingClick={handleEmbeddingClick} />
          <AILabelsSection wallet={wallet} />
          <TagsSection wallet={wallet} />
          <PerformanceMetricsSection wallet={wallet} />
          <TimestampsSection wallet={wallet} />
          <DocumentInfoSection hit={hit} wallet={wallet} />
        </CardContent>
      </Card>

      <SearchHitsDialog
        open={searchDialogOpen}
        title={`Test ${selectedEmbeddingLabel}`}
        endpoint="/search/semantic"
        payload={searchPayload}
        onClose={handleSearchDialogClose}
      />
    </ListItem>
  );
};

export default KalshiWalletCard;
